<template>
  <div id="app" class="uk-offcanvas-content">
    <AppHeader @changeLanguage="changeLanguage" />
    <AppBody />
    <AppFooter />
    <AppOffcanvas @changeLanguage="changeLanguage" />
  </div>
</template>

<script>
// Vue store (based on vue-ORM) & router -> moved on main.js (reading appConfig shared with i18n)
// import store from '@/store'

import router from '@/router'

// base layout components
import AppHeader from './_layout/Header'
import AppBody from './_layout/Body'
import AppFooter from './_layout/Footer'
import AppOffcanvas from './_layout/Offcanvas'

import { mapState, mapGetters, mapActions } from 'vuex'

import { createNewProject } from '../store/mixins'

import { validateField, parseAttributeOptions, repairELinks, getDecimalSeparator, getBrowserInfo } from '@/utils/qdev'

// import SVG from 'svg.js'

export default {
  // store,
  router,
  components: {
    AppHeader,
    AppBody,
    AppFooter,
    AppOffcanvas
  },

  data () {
    return {
      lang: this.$root.$i18n.locale,

      blankProject: false
    }
  },

  computed: {
    ...mapState([
      'languages',
      'user',
      'lsKey',
      'apiData'
    ]),

    ...mapGetters([
      'userIsGuest',
      'isEverythingSaved',
      'opcoId'
    ])

    /* visitor () {
      const user = this.$store.getters['db/user/find'](1)

      return user ? user.first_name + ' ' + user.last_name : 'Unknown Dude'
    } */
  },

  created () {
    // debug actions
    if (process.env.NODE_ENV === 'development') {
      this.$store.subscribeAction((action, state) => {
        // console.log('called action:', action.type, action.payload)
      })
    }

    let initialData = {
      projects: [],
      selected: null
    }

    // check if there's something saved locally
    let localData = null

    // can fail if user privacy mode doesnt allow localStorage
    try {
      localData = localStorage.getItem(this.lsKey)

      if (localData) {
        localData = JSON.parse(localData)
      }
    } catch (err) {
      // notify user
      // uikit.notification()
    }

    if (!this.user || this.user.is_guest) {
      if (localData) {
        // in case the user session has expired (user is guest now) we need to check if we have a backup available and restore from it as user's data might be displayed as guest and will raise calculation errors & it's not OK from any point of view ...
        const backup = window.localStorage.getItem(`${this.$store.state.lsKey}_backup`)

        if (backup) {
          initialData = JSON.parse(backup)

          // this might not be needed as will be written by data load
          /* window.localStorage.setItem(`${this.$store.state.lsKey}`, JSON.stringify(initialData.projects))

          window.localStorage.setItem(`${this.$store.state.lsKey}_selected`, initialData.selected) */

          window.localStorage.removeItem(`${this.$store.state.lsKey}_backup`)
        } else {
          initialData = this.loadFromLocal(localData)
        }
      } else {
        // just create blank data
        initialData = this.createBlankData()
      }
    } else {
      // authenticated user
      if (this.apiData !== null) {
        // default server data takes precedence
        initialData = this.loadFromAPI(this.apiData)

        // if we also have localData, then we have to check if there's something newer there
        // normally don't as we're automatically syncing everything on logout / close / browse / etc
        if (localData) {
          // const newProjects = []

          localData.forEach(localProject => {
            // do we have this project on server ?
            // const savedProject = initialData.projects.filter(apiProject => localProject.id === apiProject.id)

            // we could have used Array.prototype.findIndex() but it's not supported by IE
            // thus, simple solution to extract all ID's into an array and search for index
            const savedProjectIndex = initialData.projects.map(project => project.id).indexOf(localProject.id)

            if (savedProjectIndex > -1) {
              // found on server too, we need to compare project info first
              const savedProject = initialData.projects[savedProjectIndex]

              // update it ONLY if date_last_save match and local saved flag not anymore
              // if date is lower, it means that's old so keep the server, and it cannot have newer date then from server ... but let's do it anyway
              if (localProject.date_last_save && (localProject.date_last_save > savedProject.date_last_save || (localProject.date_last_save === savedProject.date_last_save && !localProject.savedOnServer))) {
                // copy from local, but without slabs, which will be compared individually later on
                // saved flag and date can be copied as well (I guess ...)
                for (const field in localProject) {
                  if (field !== 'slabs') {
                    initialData.projects[savedProjectIndex][field] = localProject[field]
                  }
                }
              }

              // now we need to compare each element of the localProject
              // const newSlabs = []

              localProject.slabs.forEach(localSlab => {
                // do we have this element on server ?
                const savedSlabIndex = savedProject.slabs.map(slab => slab.id).indexOf(localSlab.id)

                if (savedSlabIndex > -1) {
                  const savedSlab = savedProject.slabs[savedSlabIndex]

                  if (localSlab.date_last_save && (localSlab.date_last_save > savedSlab.date_last_save || (localSlab.date_last_save === savedSlab.date_last_save && !localSlab.savedOnServer))) {
                    // override it completely
                    initialData.projects[savedProjectIndex].slabs[savedSlabIndex] = localSlab
                  }
                } else {
                  // this is NOT (anymore ?) on server, let's mark it for import
                  // newSlabs.push(localSlab)
                  initialData.projects[savedProjectIndex].slabs.push(localSlab)
                }
              })

              // actually importing new slabs (from local)
              // initialData.projects[savedProjectIndex].slabs = initialData.projects[savedProjectIndex].slabs.concat(newSlabs)
            } else {
              // this is NOT (anymore ?) on server, let's mark it for import together with all it's elements
              // newProjects.push(localProject)
              initialData.projects.push(localProject)
            }
          })

          // actually importing new projects with their slabs (from local)
          // initialData.projects = initialData.projects.concat(newProjects)

          // if we have a selected item on local, let's set this
          try {
            const selected = localStorage.getItem(`${this.lsKey}_selected`)

            if (selected && selected !== 'null') {
              initialData.selected = selected
            }
          } catch (error) {}
        }
      } else if (localData) {
        // no server data, just use localData (if we have)
        initialData = this.loadFromLocal(localData)
      } else {
        // just create blank data
        initialData = this.createBlankData()
      }
    }

    // load prepared data (blank, local or server)
    this.$store.dispatch('db/projects/create', { data: initialData.projects }).then(() => {
      this.$store.commit('allowChanges', false)
    })

    // in case selected element belongs to a project different than the default OpCo (identified by app domain), add a blank project
    let currentProject
    let opcoElementId

    const currentElement = this.$store.getters['db/slabs/query']()
      .find(initialData.selected)

    // in case selected element doesn't exists in the elements list pick one (if possible from default OpCo project)
    if (currentElement) {
      currentProject = this.$store.getters['db/projects/query']()
        .find(currentElement.project_id)
    }

    if (!currentProject || currentProject.opco_id !== this.opcoId) {
      // try to find an existing project belonging to default OpCo
      const opcoProject = this.$store.getters['db/projects/query']()
        .where('opco_id', this.opcoId)
        .orderBy('name')
        .first()

      if (opcoProject) {
        const opcoElement = this.$store.getters['db/slabs/query']()
          .where('project_id', opcoProject.id)
          .orderBy('name')
          .first()

        if (opcoElement) {
          opcoElementId = opcoElement.id
        }
      }

      if (!opcoElementId) {
        // just create a blank project
        const blankData = createNewProject(this.$store.getters)

        // try to find a new unique project name
        const nameRegEx = new RegExp('^' + this.$t('project') + ' ', 'i')

        let blankProjectNames = this.$store.getters['db/projects/all']()
          .map(project => project.name)
          .filter(name => nameRegEx.test(name))
          .map(name => parseInt(name.replace(nameRegEx, ''), 10))
          .filter(no => no && !isNaN(no))

        if (blankProjectNames.length > 0) {
          blankData.projects.name = this.$t('project') + ' ' + (Math.max(...blankProjectNames) + 1)
        }

        this.$store.dispatch('db/projects/insert', { data: blankData.projects })

        opcoElementId = blankData.selected
      }

      if (opcoElementId) {
        initialData.selected = opcoElementId
      }
    }

    this.$store.commit('setElementId', initialData.selected)

    // if navigator language not a supported one, move it to english
    // we have locale fallback for content, but language menu classes will not work
    if (!(this.lang in this.languages)) {
      this.changeLanguage('en')
    }
  },

  mounted () {
    // handle numeric input - allow comma as decimal separator too
    // keyup cannot be prevented here, on document, only directly on input field
    document.addEventListener('keydown', (event) => {
      const input = event.target
      const separator = getDecimalSeparator()
      const browser = getBrowserInfo()
      const keys = [',']

      // Safari has a bug and
      if (browser.isSafari) {
        keys.push('.')
      }

      const decimalHack = keys.indexOf(event.key) > -1 && (event.key !== separator || browser.isSafari)

      // it seems that almost every browser besides Firefox on Mac when you try to add a zero after the decimal point it removes it (so you cannot easily type 0.01)
      const zeroHack = event.key === '0' // && !browser.isFirefox

      /* console.log(
        'decimalHack', decimalHack,
        'separator', separator,
        'key', event.key,
        'zeroHack', zeroHack
      ) */

      if (input.type === 'number' && /\./.test(input.step)) {
        // decimal separator hack
        if (decimalHack || zeroHack) {
          event.preventDefault()

          // this is not quite ok when user tries to insert decimal point in the middle of the value, but number input does not support reading selection / caret
          let newValue = input.value + (decimalHack ? separator : '0')

          input.value = newValue

          // input.selectionStart is always null for <input type='number'> so we'll try a hack by changing type to text before
          // it seems that this doesn't work anymore either as it looses focus and repositions to start
          // input.type = 'text'
          // console.log(input.selectionStart)
          // input.type = 'number'
        }
      }
    })

    // We also will call this on visibilitychange instead of on unload as proposed here https://w3c.github.io/beacon/#h-note, especially for mobile when the browser will go into background and then closed by OS
    document.addEventListener('visibilitychange', (event) => {
      if (document.visibilityState === 'hidden') {
        this.sync()
      }
    })

    window.addEventListener('beforeunload', (event) => {
      if (!this.userIsGuest && !this.isEverythingSaved) {
        this.sync().then((xhr) => {
          if (xhr.response.success) {
            uikit.notification(this.$t('can_close_window'), {pos: 'bottom-right', status: 'success'})
          } else {
            uikit.notification(this.$t('error_saving_data'), {pos: 'bottom-right', status: 'danger'})
          }
        }, () => {
          uikit.notification(this.$t('error_saving_data'), {pos: 'bottom-right', status: 'danger'})
        })

        event.returnValue = this.$t('confirm_window_close')
      }
    })

    if (this.blankProject) {
      // this is NOT a recommended Vue method, but I use it here anyway (rules are set to be broken)
      // we'll skip this as we will need to popup the guest modal
      // this.$root.$emit('blankProject')
    }

    // attach live validation
    uikit.util.on(document, 'keyup change', 'input[data-validate]:not([disabled]):not([readonly])', this.validate)

    // listening for field validation requests
    this.$root.$on('validateField', this.validate)

    // listening for entire load or opening entire row validation
    // item = {type: 'load', id: '...'}
    this.$root.$on('validateItem', this.validateItem)

    // listening for entire form validation requests
    this.$root.$on('validateForm', this.validateForm)

    // repair elinks
    repairELinks()
  },

  methods: {
    ...mapActions([
      'sync'
    ]),

    createBlankData () {
      // mark it as new project in order to open the editor
      this.blankProject = true

      return createNewProject(this.$store.getters)

      // no local data found, just create a blank project & element
      /* const newProjectID = this.nextID()
      const newElementID = this.nextID()

      let data = {
        id: newProjectID,
        name: `${this.$t('project')} 1`,
        // opco_id: 2,
        slabs: [{
          id: newElementID,
          project_id: newProjectID,
          name: `${this.$t('slab')} 1`
        }]
      }

      // if opco sent through hash, change project opco_id + element type & details
      const hash = window.location.hash.replace(/^#/, '')

      if (hash.length) {
        const companies = this.$store.state.companies
        let opcoId = Object.keys(companies).find(id => companies[id].code === hash)

        // if valid opco found
        if (opcoId) {
          opcoId = parseInt(opcoId, 10)

          const products = this.$store.getters.opcoProductTypes(opcoId)
          const productId = Object.keys(products)[0]
          const product = products[productId]
          const thickness = Object.keys(product.thickness)[0]

          data.opco_id = opcoId

          data.slabs[0] = Object.assign(data.slabs[0], {
            floor_product_type_id: productId,
            floor_thickness: thickness,
            floor_width: product.thickness[thickness][0].width,
            topping_thickness: product.thickness[thickness][0].topping_thickness[0]
          })
        }
      }

      return {
        projects: data,
        selected: newElementID
      } */
    },

    loadFromLocal (data) {
      let selected

      try {
        selected = localStorage.getItem(`${this.lsKey}_selected`)

        if (!selected || selected === 'null') {
          selected = data[0].slabs[0].id
        }
      } catch (err) {
        // if we cannot restore it from localStorage, select first element of first project
        selected = data[0].slabs[0].id
      }

      // this.$store.dispatch('db/projects/create', { data })

      // this.$store.commit('setElementId', selected)

      return {
        projects: data,
        selected
      }
    },

    loadFromAPI (data) {
      // this.$store.dispatch('db/projects/create', { data })

      // this.$store.commit('setElementId', data[0].slabs[0].id)

      return {
        projects: data,
        selected: data[0].slabs[0].id
      }
    },

    validateForm (form = null) {
      if (!form) {
        form = document
      } else if (typeof form === 'string') {
        form = document.querySelector(form)
      }

      if (form) {
        // document.querySelectorAll('input[data-validate]').forEach(this.validate)
        Array.prototype.forEach.call(form.querySelectorAll('input[data-validate]:not([disabled]):not([readonly])'), this.validate)
      }
    },

    // validates entire load or opening row
    // item = {type: 'load', id: '...'}
    validateItem (item) {
      const row = document.getElementById(`${item.type}_${item.id}`)

      if (row) {
        Array.prototype.forEach.call(row.querySelectorAll('input[data-validate]:not([disabled]):not([readonly])'), this.validate)
      }
    },

    validate (field = null) {
      if (field) {
        let error = null

        // field can be also event
        if (field instanceof Event || field.originalEvent instanceof Event) {
          field = field.target
        }

        if (typeof field === 'string') {
          field = document.querySelector(field)
        }

        // only if field is not disabled or readonly
        if (!field.getAttribute('disabled') && !field.getAttribute('readonly')) {
          const errors = validateField(field)

          if (errors.length) {
            const min = parseFloat(field.getAttribute('min') || Number.NEGATIVE_INFINITY)
            const max = parseFloat(field.getAttribute('max') || Number.POSITIVE_INFINITY)

            error = this.$t(`validations.${errors[0]}`, {
              min: min,
              max: max
            })
          }
        }

        // show first error if any or clear if none
        this.processValidation(field, error)
      }
    },

    processValidation (field, error) {
      let label

      const fieldId = field.getAttribute('id')

      // parse validation helper (uk-data style)
      let validationHelper = parseAttributeOptions(field.getAttribute('data-validate-helper'))

      if (validationHelper.label) {
        label = document.querySelector(validationHelper.label)
      }

      if (!label && fieldId) {
        label = document.querySelector(`label[for="${fieldId}"]`)
      }

      // default: last resort is to use parent sibling element
      if (!label) {
        label = field.parentElement

        // hm, trickier for loads & openings (inside a table)
        if (label.tagName !== 'TD') {
          // I guess this is for left / right support length (only one label)
          label = label.previousElementSibling
        }
      }

      if (error) {
        label.setAttribute('data-error', error)
        field.classList.add('uk-form-danger')
      } else {
        // clear error
        label.removeAttribute('data-error')
        field.classList.remove('uk-form-danger')
      }
    },

    changeLanguage (lang) {
      // this changes locale ONLY for current module, not entire app !
      // this.$i18n.locale = lang

      // this is intended only for child / parent simple communication (which can be used here)
      // https://jsfiddle.net/tiagomatosweb/vqtnpyzw/
      // this.$emit('languageChanged', lang)

      // using a separate Vue() instance as app event bus - but it's not recommended for bApps
      // http://vuetips.com/global-event-bus

      // localForage - indexDB, WebSQL, localStorage engine for offline
      // https://github.com/kazupon/vue-i18n/issues/2#issuecomment-324232096

      // we can use the Vuex as recommended, the only issue is that we need to import the i18n into vuex store definition in order to configure properly the language change
      // https://github.com/kazupon/vue-i18n/issues/2#issuecomment-324019506
      // this.$store.commit('changeLocale', lang)

      // so we come almost back, we were just missing a simple fucking $root to get on top :)
      /* window.localStorage.language = lang
      this.lang = lang
      this.$i18n.locale = lang */

      // back to recommended as we've done it properly there (I assume) but we'll use action as we might load it async based on page (route)
      this.$store.dispatch('changeLocale', {
        page: this.$route.name,
        locale: lang
      })
    },

    nextID () {
      const uuid = require('uuid-random')

      return uuid()
    }
  }
}
</script>

<style lang="less">
  @import '../assets/theme/UIkit/Echo-slabs.less';

  [data-error],
  [data-message] {
    position: relative;

    &::after {
      margin-left: 10px;
      color: #E2231A;
      font-size: 0.75rem;
      content: attr(data-error);
    }
  }

  [data-message]:not([data-error])::after {
    color: #999;
    content: attr(data-message);
  }

  @media screen and (min-width: 960px) {
    [data-error]::after,
    [data-message]::after {
      margin-left: 0;
      position: absolute;
      left: 0;
      bottom: -18px;
      /* top: 18px; */
    }

    td[data-error]::after,
    td[data-message]::after {
      left: auto;
      right: 8px;
      bottom: 3px;
    }

    .uk-grid-small > [data-error]::after,
    .uk-grid-small > [data-message]::after {
      padding-left: 15px;
    }
  }

  lnk {
    cursor: pointer;
  }

  .pzzl {
    unicode-bidi: bidi-override;
    direction: rtl;
  }
</style>



// WEBPACK FOOTER //
// src/components/App.vue